import { Injectable } from '@angular/core';
import { HttpClient } from "@angular/common/http";
import { PatientEdiaryEntry } from "../models/patient-ediary-entry.model";
import { environment } from "../../../environments/environment";
import { map } from "rxjs/internal/operators/map";
import { UpdateEdiaryConfig } from "./interfaces/update-ediary-config.interface";
import { PatientEdiaryConfig } from "../models/patient-ediary-config.model";
import { EdiaryEntryDetails } from "../models/trial-diary-form/ediary-entry-details.model";
import { AdminEdiaryEntryForm } from "../models/trial-diary-form/admin-ediary-form.model";
import { UpdatePatientEdiaryEntriesRequest } from "../models/trial-diary-form/ediary.models";
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class EDiaryService {

  private ENDPOINT = environment.apiUrl + '/patient/';

  constructor(private http: HttpClient) {
  }

  updateMazeDiaryEntry(entryId: string, fasting: boolean, takenAt: number, pillsTaken: number) {
    const body = {
      fasting,
      takenAt,
      pillsTaken
    };

    return this.http.post(environment.apiUrl + '/MazeDiary/Entry/' + entryId, body);
  }

  updatePatientEdiaryEntries(patientTrialId: string, request: UpdatePatientEdiaryEntriesRequest) {
    return this.http.put(environment.apiUrl + '/patient/' + patientTrialId + '/ediary/entries', request);
  }

  resetPatientEdiary(patientTrialId: string, diaryFormId: string) {
    return this.http.put(this.ENDPOINT + patientTrialId + '/ediary/reset?diaryFormId=' + diaryFormId, {});
  }

  getEdiaryEntryDetails(entryId: string) {
    return this.http.get<EdiaryEntryDetails>(this.ENDPOINT + 'ediary/entry/' + entryId).pipe(map(rsp => new EdiaryEntryDetails(rsp)));
  }

  getEdiaryEntryForEdit(entryId: string): Observable<AdminEdiaryEntryForm> {
    return this.http.get<AdminEdiaryEntryForm>(`${this.ENDPOINT}ediary/entry/${entryId}/editable`);
  }

  deleteEdiaryEntry(patientTrialId: string, entryId: string) {
    return this.http.delete(`${this.ENDPOINT}${patientTrialId}/ediary/entry/${entryId}`);
  }

  upsertEdiaryEntry(patientTrialId: string, payload: any) {
    return this.http.post(`${this.ENDPOINT}${patientTrialId}/ediary/entry`, payload);
  }

  /**
   * Retrieves the diary entries for a given patient trial.
   *
   * @param {string} patientTrialId - The unique identifier for the patient trial.
   * @return {Observable<PatientEdiaryEntry[]>} An observable containing an array of patient diary entries.
   */
  getEdiaryEntries(patientTrialId: string, diaryFormId: string) {
    return this.http
      .get<PatientEdiaryEntry[]>(`${this.ENDPOINT}${patientTrialId}/ediary/entries?diaryFormId=${diaryFormId}`)
      .pipe(
        map(entries => entries.map(entry => new PatientEdiaryEntry(entry)))
      );
  }

  /**
   * Updates the configuration of a patient's diary for a specified trial.
   *
   * @param {string} patientTrialId - The unique identifier of the patient's trial.
   * @param {UpdateEdiaryConfig} dto - The data transfer object containing the updated diary configuration.
   * @return {Observable<Object>} - An observable containing the response from the HTTP PUT request.
   */
  updateEdiaryConfig(patientTrialId: string, dto: UpdateEdiaryConfig, diaryFormId?: string) {
    const params = diaryFormId ? `?diaryFormId=${diaryFormId}` : '';
    return this.http.put(this.ENDPOINT + patientTrialId + '/ediary/config' + params, dto);
  }

  /**
   * Retrieves the configuration for a patient's diary based on the provided trial ID.
   *
   * @param {string} patientTrialId - The unique identifier for the patient's trial.
   * @param {string} diaryFormId - The specific diary form to retrieve config for.
   * @return {Observable<any>} - An observable containing the patient's diary configuration.
   */
  getEdiaryConfig(patientTrialId: string, diaryFormId?: string) {
    const params = diaryFormId ? `?diaryFormId=${diaryFormId}` : '';
    return this.http.get<PatientEdiaryConfig>(this.ENDPOINT + patientTrialId + '/ediary/config' + params);
  }

  simulateConditionalNextDay(patientTrialId: string, diaryFormId: string) {
    return this.http.post(`${this.ENDPOINT}${patientTrialId}/ediary/simulate-next-day?diaryFormId=${diaryFormId}`, {});
  }

  simulateNextWeek(patientTrialId: string, diaryFormId: string) {
    return this.http.post(`${this.ENDPOINT}${patientTrialId}/ediary/simulate-next-week?diaryFormId=${diaryFormId}`, {});
  }

  simulateNextMonth(patientTrialId: string, diaryFormId: string) {
    return this.http.post(`${this.ENDPOINT}${patientTrialId}/ediary/simulate-next-month?diaryFormId=${diaryFormId}`, {});
  }
}
